import { useTheme } from '@mui/material/styles';
// File: src/components/navigation/LLMProcessPanel.jsx
import React, { useState } from 'react';
import {
    Box, Typography, IconButton, TextField,
    CircularProgress, Button, List, ListItem, ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LLM_STATES, useLLMProcess } from '../../contexts/LLMProcessContext';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as Syntax } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

export default function LLMProcessPanel({ onClose }) {
    const theme = useTheme();
    const darkMode = theme.palette.mode === 'dark';
    const {
        selected, progress, status, setStatus,
        resultMarkdown, startProcess,
    } = useLLMProcess();

    // Keep draft prompts local until the user starts a job.
    const [prompt, setPrompt] = useState('');

    // Render generated Markdown with the same typography and colors as the reader.
    const mdComponents = {
        h1: ({ node, ...props }) => (
            <Typography variant="h5" sx={{ mt: 3, mb: 1, fontWeight: 600 }} {...props} />
        ),
        h2: ({ node, ...props }) => (
            <Typography variant="h6" sx={{ mt: 2.5, mb: .5, fontWeight: 600 }} {...props} />
        ),
        p: ({ node, ...props }) => (
            <Typography variant="body1" paragraph {...props} />
        ),
        code({ inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
                <Box sx={{ my: 2 }}>
                    <Syntax style={oneDark} language={match[1]} PreTag="div" {...props}>
                        {String(children).replace(/\n$/, '')}
                    </Syntax>
                </Box>
            ) : (
                <Box component="code"
                    sx={{
                        bgcolor: darkMode ? '#333' : '#f5f5f5',
                        color: darkMode ? '#ffc400' : '#d32f2f',
                        px: .5, py: .2, borderRadius: .5,
                        fontFamily: 'monospace', fontSize: '0.85rem',
                    }}
                    {...props}
                >
                    {children}
                </Box>
            );
        },
        a: ({ node, children, ...props }) => (
            <a style={{ color: theme.palette.accent.main }} {...props}>{children}</a>
        ),
        table: ({ node, ...props }) => (
            <Box component="table"
                sx={{
                    width: '100%', borderCollapse: 'collapse',
                    '& th,& td': { border: '1px solid', borderColor: darkMode ? '#555' : '#ccc', p: .75 },
                    '& th': { bgcolor: darkMode ? '#3949ab' : '#e3f2fd', fontWeight: 600 },
                }}
                {...props} />
        ),
    };

    return (
        <Box sx={{
            position: 'absolute', right: 0, top: 0, bottom: 0,
            width: { xs: '100%', sm: 620 },
            bgcolor: darkMode ? '#1e1e1e' : 'background.paper',
            boxShadow: 4, zIndex: 20, p: 2, display: 'flex', flexDirection: 'column',
            mt: { xs: 1, sm: '110px' },
        }}>
            {/* Schließen-Button */}
            <IconButton onClick={onClose}
                sx={{
                    position: 'absolute', top: 8, right: 8,
                    color: darkMode ? '#fff' : 'inherit'
                }}>
                <CloseIcon />
            </IconButton>

            <Typography sx={{ fontWeight: 600, mb: 2 }}>Process in LLM</Typography>

            {/* ––––– Konfiguration ––––– */}
            {status === LLM_STATES.config && (
                <>
                    <TextField
                        multiline fullWidth minRows={3}
                        placeholder="Enter your prompt…"
                        value={prompt}
                        onChange={e => setPrompt(e.target.value)}
                        sx={{
                            mb: 2,
                            '& .MuiInputBase-root': {
                                bgcolor: darkMode ? '#222' : undefined,
                                color: darkMode ? '#fff' : undefined,
                            },
                        }}
                    />

                    <Typography sx={{ fontWeight: 600, mb: 1 }}>Selected topics</Typography>
                    <List dense sx={{ flexGrow: 1, overflowY: 'auto', mb: 2 }}>
                        {selected.map(t => (
                            <ListItem key={t._id}><ListItemText primary={t.title} /></ListItem>
                        ))}
                    </List>

                    <Button variant="contained"
                        disabled={!prompt || selected.length === 0}
                        onClick={() => startProcess(prompt)}>
                        Start processing
                    </Button>
                </>
            )}

            {/* ––––– Loading ––––– */}
            {status === LLM_STATES.running && (
                <Box sx={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                }}>
                    <CircularProgress size={64}
                        sx={{ color: 'progress.main', mb: 2 }} />
                    <Typography>{`${progress}% – crunching…`}</Typography>
                </Box>
            )}

            {/* ––––– Ergebnis ––––– */}
            {status === LLM_STATES.done && (
                <>
                    <Box sx={{ flex: 1, flexGrow: 1, overflow: 'auto', pr: 1 }}>
                        <ReactMarkdown
                            children={resultMarkdown || '*No response*'}
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeRaw]}
                            components={mdComponents}
                        />
                    </Box>

                    <Button variant="contained"
                        onClick={() => setStatus(LLM_STATES.config)}>
                        Enter new prompt
                    </Button>
                </>
            )}

            {status === LLM_STATES.idle && (
                <Typography color="error">Process failed – see console.</Typography>
            )}
        </Box>
    );
}
